import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { BottomSheet } from '@/components/BottomSheet';
import { Button } from '@/components/primitives/Button';
import { Input } from '@/components/primitives/Input';
import { Icon } from '@/components/Icon';
import { StatusPill } from '@/components/primitives/StatusPill';
import { Spinner } from '@/components/primitives/Spinner';
import { useApproveTsRoutes, useDeleteTsNode, useExpireTsNode, useRenameTsNode, useSetTsNodeTags, useTsNode, } from '@/api/tailscale/useTailscale';
import { relativeTime } from '@/lib/relativeTime';
import s from './NodeDetailSheet.module.css';
/**
 * Node detail sheet — Phase 3 with mutations enabled.
 *
 * Action split:
 *   * Inline (tags, route approve/disapprove) — edit-in-place toggled
 *     by a button next to the section header.
 *   * Destructive (rename / expire / delete) — buttons in the footer
 *     with native `confirm()` gates. Rename prompts for new name via
 *     `prompt()` — matches existing backup-service UX; ConfirmDialog
 *     upgrade is on the roadmap.
 */
function Copyable({ value, className, children, }) {
    const [copied, setCopied] = useState(false);
    const click = () => {
        navigator.clipboard?.writeText(value).then(() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1200);
        }, () => { });
    };
    return (_jsxs("span", { role: "button", tabIndex: 0, onClick: click, onKeyDown: (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                click();
            }
        }, title: copied ? 'Copied' : 'Click to copy', "data-copied": copied || undefined, className: `${s.copyable} ${className ?? ''}`, children: [children ?? value, copied && _jsx("span", { className: s.copiedHint, children: "copied" })] }));
}
function fmtAbs(iso) {
    if (!iso)
        return '—';
    // Headscale uses `0001-01-01T...` for "never" (Go zero-value Time).
    if (iso.startsWith('0001-'))
        return '—';
    try {
        return new Date(iso).toLocaleString();
    }
    catch {
        return iso;
    }
}
export function NodeDetailSheet({ nodeId, onClose, }) {
    const q = useTsNode(nodeId);
    const renameM = useRenameTsNode();
    const expireM = useExpireTsNode();
    const deleteM = useDeleteTsNode();
    const tagsM = useSetTsNodeTags();
    const routesM = useApproveTsRoutes();
    const n = q.data?.node;
    // Local draft for tag editing; populated from upstream when entering
    // edit mode. `null` = not editing.
    const [tagDraft, setTagDraft] = useState(null);
    const [tagInput, setTagInput] = useState('');
    const [actionErr, setActionErr] = useState(null);
    // Reset edit state when the sheet swaps to a different node.
    useEffect(() => {
        setTagDraft(null);
        setTagInput('');
        setActionErr(null);
    }, [nodeId]);
    const startTagEdit = () => {
        if (!n)
            return;
        setTagDraft([...n.forcedTags]);
        setTagInput('');
    };
    const cancelTagEdit = () => {
        setTagDraft(null);
        setTagInput('');
    };
    const addTagFromInput = () => {
        if (tagDraft === null)
            return;
        const raw = tagInput.trim();
        if (!raw)
            return;
        // Headscale tags must start with `tag:`. Auto-prefix on user
        // forgetting — common mistake.
        const t = raw.startsWith('tag:') ? raw : `tag:${raw}`;
        if (tagDraft.includes(t)) {
            setTagInput('');
            return;
        }
        setTagDraft([...tagDraft, t]);
        setTagInput('');
    };
    const removeTagFromDraft = (t) => {
        if (tagDraft === null)
            return;
        setTagDraft(tagDraft.filter((x) => x !== t));
    };
    const saveTags = async () => {
        if (tagDraft === null || !n)
            return;
        setActionErr(null);
        try {
            await tagsM.mutateAsync({ nodeId: n.id, tags: tagDraft });
            setTagDraft(null);
        }
        catch (e) {
            setActionErr(e.message ?? 'Failed to save tags');
        }
    };
    const toggleRoute = async (prefix) => {
        if (!n)
            return;
        setActionErr(null);
        const isApproved = n.approvedRoutes.includes(prefix);
        const next = isApproved
            ? n.approvedRoutes.filter((r) => r !== prefix)
            : [...n.approvedRoutes, prefix];
        try {
            await routesM.mutateAsync({ nodeId: n.id, routes: next });
        }
        catch (e) {
            setActionErr(e.message ?? 'Failed to update routes');
        }
    };
    const doRename = async () => {
        if (!n)
            return;
        const proposed = window.prompt(`Rename node "${n.givenName || n.name}" to:`, n.givenName || '');
        if (proposed === null)
            return;
        const trimmed = proposed.trim();
        if (!trimmed || trimmed === n.givenName)
            return;
        setActionErr(null);
        try {
            await renameM.mutateAsync({ nodeId: n.id, newName: trimmed });
        }
        catch (e) {
            setActionErr(e.message ?? 'Failed to rename');
        }
    };
    const doExpire = async () => {
        if (!n)
            return;
        if (!window.confirm(`Expire node "${n.givenName || n.name}"?\n\nThe device will be forced to re-authenticate. Existing connections drop.`))
            return;
        setActionErr(null);
        try {
            await expireM.mutateAsync(n.id);
        }
        catch (e) {
            setActionErr(e.message ?? 'Failed to expire');
        }
    };
    const doDelete = async () => {
        if (!n)
            return;
        const label = n.givenName || n.name;
        if (!window.confirm(`Delete node "${label}" from the tailnet?\n\nThis removes it permanently. The device must re-register with a preauth key to reconnect. Cannot be undone.`))
            return;
        setActionErr(null);
        try {
            await deleteM.mutateAsync(n.id);
            onClose();
        }
        catch (e) {
            setActionErr(e.message ?? 'Failed to delete');
        }
    };
    const anyPending = renameM.isPending || expireM.isPending || deleteM.isPending || tagsM.isPending;
    return (_jsxs(BottomSheet, { title: _jsxs("div", { className: s.statusBar, children: [_jsx("span", { children: n ? n.givenName || n.name : 'Node' }), n && (_jsx(StatusPill, { tone: n.online ? 'ok' : 'paused', children: n.online ? 'online' : 'offline' }))] }), subtitle: n ? (_jsxs("span", { className: s.headSubtitle, children: ["id ", n.id, " \u00B7 ", n.user.name, " \u00B7 ", n.ipAddresses[0] ?? '—'] })) : null, onClose: onClose, footer: _jsxs("div", { className: s.footActions, children: [_jsx(Button, { variant: "ghost", onClick: onClose, children: "Close" }), _jsxs("div", { className: s.footRight, children: [_jsxs(Button, { variant: "ghost", onClick: doRename, disabled: !n || anyPending, children: [renameM.isPending ? _jsx(Spinner, { size: 12 }) : _jsx(Icon, { name: "edit", size: 14 }), "Rename"] }), _jsxs(Button, { variant: "ghost", onClick: doExpire, disabled: !n || anyPending, children: [expireM.isPending ? _jsx(Spinner, { size: 12 }) : _jsx(Icon, { name: "warn", size: 14 }), "Expire"] }), _jsxs(Button, { variant: "danger", onClick: doDelete, disabled: !n || anyPending, children: [deleteM.isPending ? _jsx(Spinner, { size: 12 }) : _jsx(Icon, { name: "trash", size: 14 }), "Delete"] })] })] }), children: [q.isLoading && (_jsxs("div", { className: s.placeholder, children: [_jsx(Spinner, { size: 14 }), " Loading\u2026"] })), q.error && (_jsx("div", { className: s.errBlock, children: q.error.message ?? 'Failed to load node' })), actionErr && _jsx("div", { className: s.errBlock, children: actionErr }), n && (_jsxs(_Fragment, { children: [_jsxs("div", { className: s.section, children: [_jsx("div", { className: s.sectionHead, children: "Overview" }), _jsx(KvRow, { k: "Given name", v: n.givenName || _jsx("span", { className: s.muted, children: "\u2014" }) }), _jsx(KvRow, { k: "Headscale name", v: _jsx(Copyable, { value: n.name, className: `${s.kvValue} ${s.kvMono}` }) }), _jsx(KvRow, { k: "User", v: n.user.name }), _jsx(KvRow, { k: "Register method", v: n.registerMethod.replace('REGISTER_METHOD_', '').toLowerCase() })] }), _jsxs("div", { className: s.section, children: [_jsx("div", { className: s.sectionHead, children: "Connectivity" }), _jsx(KvRow, { k: "IP addresses", v: _jsx("div", { className: s.ipList, children: n.ipAddresses.map((ip) => (_jsx(Copyable, { value: ip, className: s.kvMono }, ip))) }) }), _jsx(KvRow, { k: "Last seen", v: n.online ? (_jsx("span", { className: s.onlineNow, children: "online now" })) : (_jsxs(_Fragment, { children: [fmtAbs(n.lastSeen), _jsxs("span", { className: s.muted, children: [" \u00B7 ", relativeTime(n.lastSeen)] })] })) }), _jsx(KvRow, { k: "Expiry", v: n.expiry.startsWith('0001-') ? (_jsx("span", { className: s.muted, children: "never (does not expire)" })) : (fmtAbs(n.expiry)) }), _jsx(KvRow, { k: "Created", v: fmtAbs(n.createdAt) })] }), _jsxs("div", { className: s.section, children: [_jsxs("div", { className: s.sectionHeadRow, children: [_jsx("span", { className: s.sectionHead, children: "Tags" }), tagDraft === null ? (_jsx("button", { type: "button", className: s.editLink, onClick: startTagEdit, disabled: anyPending, children: "Edit" })) : (_jsxs("div", { className: s.editActions, children: [_jsx("button", { type: "button", className: s.editLink, onClick: cancelTagEdit, disabled: tagsM.isPending, children: "Cancel" }), _jsx("button", { type: "button", className: `${s.editLink} ${s.editLinkPrimary}`, onClick: saveTags, disabled: tagsM.isPending, children: tagsM.isPending ? 'Saving…' : 'Save' })] }))] }), tagDraft === null ? (_jsxs(_Fragment, { children: [_jsx(KvRow, { k: "Forced (admin)", v: n.forcedTags.length === 0 ? (_jsx("span", { className: s.muted, children: "none" })) : (_jsx("div", { className: s.tagList, children: n.forcedTags.map((t) => (_jsx("span", { className: s.tag, children: t }, t))) })) }), n.validTags.length > 0 && (_jsx(KvRow, { k: "Valid", v: _jsx("div", { className: s.tagList, children: n.validTags.map((t) => (_jsx("span", { className: s.tag, children: t }, t))) }) })), n.invalidTags.length > 0 && (_jsx(KvRow, { k: "Invalid", v: _jsx("div", { className: s.tagList, children: n.invalidTags.map((t) => (_jsx("span", { className: `${s.tag} ${s.tagInvalid}`, children: t }, t))) }) }))] })) : (_jsxs("div", { className: s.tagEditor, children: [_jsxs("div", { className: s.tagList, children: [tagDraft.length === 0 && _jsx("span", { className: s.muted, children: "no tags" }), tagDraft.map((t) => (_jsxs("span", { className: s.tagEditable, children: [t, _jsx("button", { type: "button", className: s.tagRemove, onClick: () => removeTagFromDraft(t), "aria-label": `Remove ${t}`, children: _jsx(Icon, { name: "x", size: 10 }) })] }, t)))] }), _jsxs("div", { className: s.tagAddRow, children: [_jsx(Input, { value: tagInput, onChange: (e) => setTagInput(e.target.value), placeholder: "tag:admin", onKeyDown: (e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        addTagFromInput();
                                                    }
                                                } }), _jsxs(Button, { variant: "ghost", type: "button", onClick: addTagFromInput, disabled: !tagInput.trim(), children: [_jsx(Icon, { name: "plus", size: 14 }), " Add"] })] }), _jsxs("div", { className: s.hint, children: ["Headscale tags must start with ", _jsx("code", { children: "tag:" }), " and the prefix must be owned by an admin in the ACL policy. The prefix is auto-added if you forget."] })] }))] }), (n.availableRoutes.length > 0 || n.approvedRoutes.length > 0) && (_jsxs("div", { className: s.section, children: [_jsx("div", { className: s.sectionHead, children: "Routes" }), _jsx("div", { className: s.routesList, children: Array.from(new Set([...n.availableRoutes, ...n.approvedRoutes])).map((r) => {
                                    const advertised = n.availableRoutes.includes(r);
                                    const approved = n.approvedRoutes.includes(r);
                                    const active = n.subnetRoutes.includes(r);
                                    return (_jsxs("div", { className: s.routeRow, children: [_jsx("span", { className: s.kvMono, children: r }), _jsxs("div", { className: s.routeMeta, children: [active && _jsx(StatusPill, { tone: "ok", children: "active" }), !active && approved && (_jsx(StatusPill, { tone: "paused", children: "approved \u00B7 offline" })), !approved && advertised && (_jsx(StatusPill, { tone: "queued", children: "pending" }))] }), _jsx(Button, { variant: "ghost", onClick: () => toggleRoute(r), disabled: routesM.isPending, children: approved ? 'Disapprove' : 'Approve' })] }, r));
                                }) })] })), _jsxs("div", { className: s.section, children: [_jsx("div", { className: s.sectionHead, children: "Identity" }), _jsx(KvRow, { k: "Node ID", v: _jsx(Copyable, { value: n.id, className: s.kvMono, children: n.id }) }), _jsx(KvRow, { k: "Machine key", v: _jsxs(Copyable, { value: n.machineKey, className: s.kvMono, children: [n.machineKey.slice(0, 20), "\u2026"] }) }), _jsx(KvRow, { k: "Node key", v: _jsxs(Copyable, { value: n.nodeKey, className: s.kvMono, children: [n.nodeKey.slice(0, 20), "\u2026"] }) }), _jsx(KvRow, { k: "Disco key", v: _jsxs(Copyable, { value: n.discoKey, className: s.kvMono, children: [n.discoKey.slice(0, 20), "\u2026"] }) })] })] }))] }));
}
function KvRow({ k, v }) {
    return (_jsxs("div", { className: s.kvRow, children: [_jsx("span", { className: s.kvKey, children: k }), _jsx("span", { className: s.kvValue, children: v })] }));
}
